import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, FileText, Clock, UserRound } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { useGetEmployeeOneOnOnes } from '@/shared/hooks/useOneOnOne';
import { Button } from '@/shared/components/ui/Button';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import { EmployeeListSidebar } from './components/EmployeeListSidebar';
import { EmployeeMeetingHistory } from './components/EmployeeMeetingHistory';
import { CreateCoworkerOneOnOneModal } from './components/CreateCoworkerOneOnOneModal';
import { ReplaceOpenMeetingDialog } from './components/ReplaceOpenMeetingDialog';
import { useOneOnOneMeetings } from './shared/hooks/useOneOnOneMeetings';
import { useCreateOneOnOneWithReplace } from './shared/hooks/useCreateOneOnOneWithReplace';
import type { DisplayEmployeeWithRole } from './shared/types/one-on-one.types';

export const OneOnOneLanding = () => {
  const { t, i18n } = useTranslation('oneOnOne');
  const currentEmployee = useEmployeeContext();

  const [isCoworkerModalOpen, setIsCoworkerModalOpen] = useState(false);

  const {
    createMeeting,
    confirmReplace,
    cancelReplace,
    isCreating,
    isReplacing,
    replaceDialogState,
  } = useCreateOneOnOneWithReplace();

  const {
    manager,
    managedEmployees,
    isLoadingManager,
    isLoadingEmployees,
    allEmployees,
    professionalMeetingsAsManager,
  } = useOneOnOneMeetings();

  const [displayEmployee, setDisplayEmployee] =
    useState<DisplayEmployeeWithRole | null>(null);

  const { data: myMeetings, isLoading: isLoadingMyMeetings } =
    useGetEmployeeOneOnOnes({
      employeeId: currentEmployee._id,
    });

  useEffect(() => {
    if (
      (isLoadingManager && !!currentEmployee.workInfo.managerId) ||
      displayEmployee
    )
      return;

    if (manager && !!myMeetings) {
      setDisplayEmployee({
        _id: manager._id,
        firstName: manager.personalInfo.firstName,
        lastName: manager.personalInfo.lastName,
        imageUrl: manager.personalInfo.imageUrl,
        role: manager.role,
        managerId: manager.workInfo.managerId,
        lastMeetingDate: myMeetings.lastMeetingDate || undefined,
        daysSinceLastMeeting: myMeetings.daysSinceLastMeeting || undefined,
        allMeetings: myMeetings.meetings || [],
        meetingRole: 'employee',
      });

      return;
    }

    if (managedEmployees?.[0]) {
      setDisplayEmployee({
        ...managedEmployees[0],
        meetingRole: 'manager',
      });
    }
  }, [
    displayEmployee,
    managedEmployees,
    manager,
    myMeetings,
    isLoadingManager,
    currentEmployee,
  ]);

  const hasDirectReports = managedEmployees && managedEmployees.length > 0;

  // Handle creating one-on-one
  const handleCreateManagerOneOnOne = async () => {
    if (isCreating || !displayEmployee) return;

    await createMeeting({
      employeeId: currentEmployee._id,
      managerId: displayEmployee._id,
      companyId: currentEmployee.companyId,
      employeeName: `${displayEmployee.firstName} ${displayEmployee.lastName}`,
    });
  };

  const handleCreateWithManager = async () => {
    if (!manager) return;

    await createMeeting({
      employeeId: currentEmployee._id,
      managerId: manager._id,
      companyId: currentEmployee.companyId,
      employeeName: `${manager.personalInfo.firstName} ${manager.personalInfo.lastName}`,
    });
  };

  const displayMeetings = displayEmployee?.allMeetings?.map((meeting) => ({
    _id: meeting._id,
    schedualedDate: meeting.schedualedDate,
    status: meeting.status,
    managerId: meeting.managerId,
  }));
  const displayIsLoading = hasDirectReports
    ? isLoadingEmployees
    : isLoadingMyMeetings;

  // Compute employee header data
  const totalMeetings = displayMeetings?.length || 0;
  const lastMeetingDate = displayEmployee?.lastMeetingDate;
  const showCreateButton = displayEmployee?.meetingRole === 'employee';

  const locales = { en: enUS, he: he };
  const lastMeetingText = lastMeetingDate
    ? formatDistanceToNow(new Date(lastMeetingDate), {
        addSuffix: true,
        locale: locales[i18n.language as 'en' | 'he'] || enUS,
      })
    : t('never');

  return (
    <div className="flex min-h-screen flex-col">
      {/* Full-width gradient banner */}
      <div className="h-28 bg-gradient-to-r from-brand-gradient-start via-brand-gradient-mid to-brand-gradient-end md:h-32 rtl:bg-gradient-to-l" />

      {/* Two-column layout — pulled up to overlap gradient (60% on gradient, 40% on bg) */}
      <div className="-mt-[60px] flex flex-1 flex-col px-4 pb-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex flex-1 w-full max-w-[86rem] flex-col gap-6 lg:flex-row">
          {/* Sidebar — floating card at inline-start, stretches to match content */}
          <div className="flex w-full shrink-0 flex-col lg:w-[300px]">
            <EmployeeListSidebar
              managedEmployees={managedEmployees}
              displayEmployee={displayEmployee}
              onSelectEmployee={setDisplayEmployee}
              manager={manager}
              managerMeetings={myMeetings?.meetings}
              onCreateMeetingWithManager={handleCreateWithManager}
              isCreatingWithManager={isCreating}
              professionalMeetingsAsManager={professionalMeetingsAsManager}
              allEmployees={allEmployees}
              onCreateMeetingWithCoworker={() => setIsCoworkerModalOpen(true)}
            />
          </div>

          {/* Main content */}
          <div className="flex flex-1 min-w-0 flex-col gap-6">
            {/* Employee header card */}
            <div className="rounded-xl border border-gray-200 bg-white px-6 py-5 shadow-sm">
              {displayEmployee ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {/* Avatar in rounded square container */}
                    <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                      <UserSelectorAvatar
                        fullName={`${displayEmployee.firstName} ${displayEmployee.lastName}`}
                        imageUrl={displayEmployee.imageUrl}
                        size="auto"
                        shape="square"
                      />
                    </div>

                    <div>
                      {/* Name + badges */}
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-bold text-gray-900">
                          {displayEmployee.firstName} {displayEmployee.lastName}
                        </h2>
                        <span className="rounded-full border border-primary/30 bg-primary/5 px-3 py-0.5 text-xs font-medium text-primary">
                          {displayEmployee.role}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                          1:1
                        </span>
                      </div>

                      {/* Meta info */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5" />
                          <span>
                            {totalMeetings}{' '}
                            {totalMeetings === 1
                              ? t('totalMeetingSingular')
                              : t('totalMeetingsPlural')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          <span>
                            {t('lastMeeting')}: {lastMeetingText}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action button */}
                  {showCreateButton && (
                    <Button
                      onClick={handleCreateManagerOneOnOne}
                      disabled={isCreating}
                      className="shrink-0 gap-2"
                    >
                      {isCreating ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          {t('creating')}
                        </>
                      ) : (
                        <>
                          <Calendar className="h-4 w-4" />
                          {t('newMeeting')}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-4 py-3">
                  <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50">
                    <UserRound className="h-8 w-8 text-gray-300" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-400">
                      {t('selectEmployeeToView')}
                    </h2>
                  </div>
                </div>
              )}
            </div>

            {/* Meeting history — fills remaining height */}
            <EmployeeMeetingHistory
              selectedEmployee={displayEmployee}
              meetings={displayMeetings}
              isLoading={displayIsLoading}
            />
          </div>
        </div>
      </div>

      <CreateCoworkerOneOnOneModal
        open={isCoworkerModalOpen}
        onOpenChange={setIsCoworkerModalOpen}
      />

      {replaceDialogState && (
        <ReplaceOpenMeetingDialog
          open={!!replaceDialogState}
          onOpenChange={cancelReplace}
          employeeName={replaceDialogState.employeeName}
          onConfirmReplace={confirmReplace}
          isReplacing={isReplacing}
        />
      )}
    </div>
  );
};
